/* One-tap "order everything low": opens the combined Amazon cart link, lists the rest, then marks all restocked. */
import { useEffect, useState } from "react";
import { api, fmtMoney, formatErr } from "@/lib/api";
import { ShoppingCart, ExternalLink, Check, Loader2 } from "lucide-react";

export default function RestockOrderPanel({ onRestocked }) {
  const [plan, setPlan] = useState(null);
  const [started, setStarted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => { api.get("/inventory/restock-plan").then((r) => setPlan(r.data)).catch(() => {}); }, []);
  if (!plan || plan.low_items.length === 0) return null;
  if (done) return <div className="w-full mt-2 border-t border-cream/15 pt-3 text-xs text-gold inline-flex items-center gap-1.5" data-testid="restock-order-msg"><Check className="w-3.5 h-3.5" /> {msg}</div>;

  const orderAll = () => {
    const first = plan.cart_url || plan.open_links[0]?.url;
    if (first) window.open(first, "_blank", "noopener");
    setStarted(true);
  };
  const markAll = async () => {
    setBusy(true); setMsg("");
    try { const { data } = await api.post("/inventory/restock-low"); setMsg(`${data.restocked} item${data.restocked === 1 ? "" : "s"} marked full — pantry and closet are topped up.`); onRestocked?.(data); setDone(true); }
    catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const rest = plan.cart_url ? plan.open_links : plan.open_links.slice(1);

  return (
    <div className="w-full mt-2 border-t border-cream/15 pt-4" data-testid="restock-order">
      {!started ? (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="text-xs text-cream/80">
            <strong className="text-cream">{plan.low_items.length} item{plan.low_items.length === 1 ? "" : "s"} running low</strong> · {fmtMoney(plan.total_to_restock)} to restock
            {plan.cart_count > 0 && <span> · {plan.cart_units} unit{plan.cart_units === 1 ? "" : "s"} of {plan.cart_count} go straight into one Amazon cart</span>}
          </div>
          <button onClick={orderAll} className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-medium bg-cream text-ink hover:bg-white" data-testid="restock-order-all">
            <ShoppingCart className="w-3.5 h-3.5" /> Order all low items on Amazon
          </button>
        </div>
      ) : (
        <div className="space-y-2" data-testid="restock-order-steps">
          {plan.cart_url && (
            <div className="text-xs text-cream/80 inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-gold" /> Amazon cart opened with {plan.cart_units} unit{plan.cart_units === 1 ? "" : "s"} ({plan.low_items.filter((i) => i.asin).map((i) => `${i.name} ×${i.restock_qty}`).join(", ")}) — review and check out.
              <a href={plan.cart_url} target="_blank" rel="noreferrer" className="underline underline-offset-2 text-gold" data-testid="restock-cart-link">Reopen cart</a></div>
          )}
          {rest.length > 0 && (
            <div className="text-xs text-cream/80">
              <div className="mb-1">{plan.cart_url ? "These need their own tab (no product ID on the link):" : "Open each remaining item:"}</div>
              <div className="flex flex-wrap gap-2">
                {rest.map((l) => (
                  <a key={l.id} href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-cream/30 px-2.5 py-1 hover:bg-cream/10" data-testid={`restock-open-${l.id}`}>{l.name} <span className="font-mono text-gold">×{l.qty}</span> <ExternalLink className="w-3 h-3" /></a>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap pt-1">
            <button onClick={markAll} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md px-4 py-2 text-xs font-medium bg-gold text-ink hover:bg-gold-deep disabled:opacity-50" data-testid="restock-mark-all">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Ordered — mark all as restocked
            </button>
            {msg && <span className="text-xs text-gold" data-testid="restock-order-msg">{msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
